import { useState, useEffect, useMemo } from "react";
import {
  Stack, Box, Flex, Text, Table, Thead, Tr, Th, Tbody, Td, Badge, IconButton,
  Menu, MenuButton, MenuList, MenuItem, Tabs, TabList, Tab, Input, InputGroup,
  InputLeftElement, Select, Button, useToast, Skeleton, useDisclosure, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  FormControl, FormLabel, Textarea, Grid, GridItem, HStack, VStack, Divider, Heading,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiMoreVertical, FiSearch, FiRefreshCw } from "react-icons/fi";
import { fetchTickets, fetchAdminUsers, fetchRoles, assignTicket, updateTicket, closeTicket, createTicket } from "./TicketActions";

const Complaints = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, totalPages: 1, limit: 10 });
  const [statusFilter, setStatusFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [stats, setStats] = useState({ total: 0, resolved: 0, unresolved: 0 });
  const { isOpen: isCreateOpen, onOpen: onCreateOpen, onClose: onCreateClose } = useDisclosure();
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const { isOpen: isDetailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [newTicket, setNewTicket] = useState({ type: "Customer", category: "Billing", priority: "Medium", subject: "", description: "" });

  const statusColorMap = { Open: "red", "In Progress": "yellow", "Waiting for Customer": "orange", Resolved: "green", Closed: "gray" };
  const priorityColorMap = { Urgent: "red", High: "orange", Medium: "blue", Low: "gray" };
  const categoryOptions = ["Billing", "Technical", "Booking", "Account", "Feedback", "Other"];
  const typeOptions = ["Customer", "Driver", "General"];
  const priorityOptions = ["Low", "Medium", "High", "Urgent"];
  const statusOptions = ["Open", "In Progress", "Waiting for Customer", "Resolved", "Closed"];

  const fetchTicketsData = async () => {
    setLoading(true);
    try {
      const filters = { page: pagination.currentPage, limit: pagination.limit };
      if (statusFilter) filters.status = statusFilter;
      if (priorityFilter) filters.priority = priorityFilter;
      if (categoryFilter) filters.category = categoryFilter;
      if (typeFilter) filters.type = typeFilter;
      if (searchInput) filters.search = searchInput;
      const response = await fetchTickets(filters);
      if (response.success) {
        setTickets(response.data || []);
        if (response.pagination) setPagination(response.pagination);
        const allTickets = response.data || [];
        const resolved = allTickets.filter(t => t.status === "Closed").length;
        const unresolved = allTickets.filter(t => t.status !== "Closed").length;
        setStats({ total: response.pagination?.total || 0, resolved, unresolved });
      }
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.message || "Failed to fetch tickets", status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  const fetchRolesData = async () => {
    try {
      const response = await fetchRoles();
      if (response.success) setRoles(response.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const fetchAdminsByRole = async (role) => {
    try {
      const response = await fetchAdminUsers(role);
      if (response.success) setAdmins(response.data || []);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  useEffect(() => { fetchTicketsData(); }, [pagination.currentPage, pagination.limit, statusFilter, priorityFilter, categoryFilter, typeFilter, refreshTrigger]);
  useEffect(() => { fetchRolesData(); }, []);
  useEffect(() => {
    if (selectedRole) { fetchAdminsByRole(selectedRole); setSelectedAdmin(""); }
    else { setAdmins([]); setSelectedAdmin(""); }
  }, [selectedRole]);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== "" || pagination.currentPage !== 1) {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
        setRefreshTrigger(prev => prev + 1);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredTickets = useMemo(() => {
    if (!searchInput) return tickets;
    const search = searchInput.toLowerCase();
    return tickets.filter(ticket =>
      ticket.ticketId?.toLowerCase().includes(search) || ticket.subject?.toLowerCase().includes(search) ||
      ticket.description?.toLowerCase().includes(search) || ticket.createdBy?.name?.toLowerCase().includes(search) ||
      ticket.createdBy?.mobile?.toLowerCase().includes(search)
    );
  }, [tickets, searchInput]);

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast({ title: "Validation Error", description: "Subject and description are required", status: "warning", duration: 3000 });
      return;
    }
    try {
      await createTicket(newTicket);
      toast({ title: "Success", description: "Ticket created successfully", status: "success", duration: 3000 });
      onCreateClose();
      setNewTicket({ type: "Customer", category: "General", priority: "Medium", subject: "", description: "" });
      fetchTicketsData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.message || "Failed to create ticket", status: "error", duration: 3000 });
    }
  };

  const handleAssignTicket = async () => {
    if (!selectedTicket || !selectedAdmin) return;
    try {
      await assignTicket(selectedTicket._id, selectedAdmin);
      toast({ title: "Success", description: "Ticket assigned successfully", status: "success", duration: 3000 });
      onAssignClose();
      setSelectedTicket(null); setSelectedRole(""); setSelectedAdmin("");
      fetchTicketsData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.message || "Failed to assign ticket", status: "error", duration: 3000 });
    }
  };

  const handleUpdateStatus = async (ticketId, newStatus) => {
    try {
      await updateTicket(ticketId, { status: newStatus });
      toast({ title: "Success", description: `Ticket status updated to ${newStatus}`, status: "success", duration: 3000 });
      fetchTicketsData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.message || "Failed to update status", status: "error", duration: 3000 });
    }
  };

  const handleCloseTicket = async (ticketId) => {
    try {
      await closeTicket(ticketId);
      toast({ title: "Success", description: "Ticket closed successfully", status: "success", duration: 3000 });
      fetchTicketsData();
    } catch (error) {
      toast({ title: "Error", description: error?.response?.data?.message || "Failed to close ticket", status: "error", duration: 3000 });
    }
  };

  const handleViewDetails = (ticket) => navigate(`/support/complaints/${ticket._id}`);

  const handleAssignClick = (ticket) => {
    setSelectedTicket(ticket);
    setSelectedRole(ticket.assignedTo?.role || "");
    if (ticket.assignedTo?._id) { fetchAdminsByRole("").then(() => { setSelectedAdmin(ticket.assignedTo._id); }); }
    else { setSelectedAdmin(""); }
    onAssignOpen();
  };

  const getStatusTabIndex = () => {
    if (!statusFilter) return 0;
    const index = statusOptions.findIndex(s => s.toLowerCase() === statusFilter.toLowerCase());
    return index >= 0 ? index + 1 : 0;
  };

  const handleTabChange = (index) => {
    setStatusFilter(index === 0 ? "" : statusOptions[index - 1]);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <Stack p={6} gap={2}>
      <BreadCrumbMaker items={[{ link: "/support", title: "Support Center" }, { link: "#", title: "Complaints", on: true }]} />

      {/* Summary Cards */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4}>
        <Box bg="white" p={4} borderRadius="xl" shadow="sm" borderLeft="4px" borderLeftColor="blue.500">
          <Text fontSize="sm" color="gray.500" fontWeight="medium">Total Complaints</Text>
          <Text fontSize="2xl" fontWeight="bold" color="blue.600">{stats.total}</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="xl" shadow="sm" borderLeft="4px" borderLeftColor="green.500">
          <Text fontSize="sm" color="gray.500" fontWeight="medium">Resolved</Text>
          <Text fontSize="2xl" fontWeight="bold" color="green.600">{stats.resolved}</Text>
        </Box>
        <Box bg="white" p={4} borderRadius="xl" shadow="sm" borderLeft="4px" borderLeftColor="red.500">
          <Text fontSize="sm" color="gray.500" fontWeight="medium">Unresolved</Text>
          <Text fontSize="2xl" fontWeight="bold" color="red.600">{stats.unresolved}</Text>
        </Box>
      </Grid>

      {/* Filters Row */}
      <Box bg="white" borderRadius="xl" p={4} shadow="sm">
        <Flex justify="space-between" align="center" mb={2} flexWrap="wrap" gap={3}>
          <Heading size="md" color="gray.700">Complaints</Heading>
          <HStack spacing={3}>
            <InputGroup maxW="250px">
              <InputLeftElement><FiSearch color="gray" /></InputLeftElement>
              <Input placeholder="Search tickets..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} borderRadius="lg" />
            </InputGroup>
            <Select placeholder="Priority" maxW="140px" value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPagination(prev => ({ ...prev, currentPage: 1 })); }}>
              {priorityOptions.map(p => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select placeholder="Category" maxW="140px" value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setPagination(prev => ({ ...prev, currentPage: 1 })); }}>
              {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
            </Select>
            <Select placeholder="Type" maxW="140px" value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPagination(prev => ({ ...prev, currentPage: 1 })); }}>
              {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
            </Select>
            <Button maxW="145px" leftIcon={<FiRefreshCw />} variant="outline" onClick={() => setRefreshTrigger(prev => prev + 1)}>Refresh</Button>
            <Button width="150px" colorScheme="blue" onClick={onCreateOpen}>Add Ticket</Button>
          </HStack>
        </Flex>

        <Tabs variant="soft-rounded" colorScheme="blue" index={getStatusTabIndex()} onChange={handleTabChange} mb={4}>
          <TabList><Tab>All</Tab>{statusOptions.map(status => <Tab key={status}>{status}</Tab>)}</TabList>
        </Tabs>

        {/* Tickets Table */}
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr><Th>Ticket ID</Th><Th>Type</Th><Th>Subject</Th><Th>Category</Th><Th>Priority</Th><Th>Status</Th><Th>Created By</Th><Th>Assigned To</Th><Th>Created At</Th><Th>Actions</Th></Tr>
            </Thead>
            <Tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <Tr key={i}>{Array.from({ length: 10 }).map((_, j) => <Td key={j}><Skeleton height="20px" /></Td>)}</Tr>
                ))
              ) : filteredTickets.length === 0 ? (
                <Tr><Td colSpan={10} textAlign="center" py={8}><Text color="gray.500">No complaints found</Text></Td></Tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <Tr key={ticket._id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => handleViewDetails(ticket)}>
                    <Td fontWeight="medium" color="blue.600">{ticket.ticketId}</Td>
                    <Td><Badge colorScheme={ticket.type === "Customer" ? "blue" : ticket.type === "Driver" ? "purple" : "gray"}>{ticket.type}</Badge></Td>
                    <Td fontWeight="medium" maxW="200px" height="66px" minH={50} noOfLines={2}>{ticket.subject}</Td>
                    <Td><Badge colorScheme="teal">{ticket.category}</Badge></Td>
                    <Td><Badge colorScheme={priorityColorMap[ticket.priority] || "gray"}>{ticket.priority}</Badge></Td>
                    <Td><Badge colorScheme={statusColorMap[ticket.status] || "gray"}>{ticket.status}</Badge></Td>
                    <Td><VStack align="start" spacing={0}><Text fontSize="sm" fontWeight="medium">{ticket.createdBy?.name || "-"}</Text><Text fontSize="xs" color="gray.500">{ticket.createdBy?.mobile || "-"}</Text></VStack></Td>
                    <Td><Text fontSize="sm">{ticket.assignedTo?.name || "Unassigned"}</Text></Td>
                    <Td fontSize="sm">{formatDate(ticket.createdAt)}</Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <Menu>
                        <MenuButton as={IconButton} icon={<FiMoreVertical />} variant="ghost" size="sm" />
                        <MenuList>
                          <MenuItem onClick={() => handleViewDetails(ticket)}>View Details</MenuItem>
                          <MenuItem onClick={() => handleAssignClick(ticket)}>Assign</MenuItem>
                          <MenuItem onClick={() => handleUpdateStatus(ticket._id, "In Progress")}>Mark In Progress</MenuItem>
                          <MenuItem onClick={() => handleUpdateStatus(ticket._id, "Resolved")}>Mark Resolved</MenuItem>
                          {ticket.status !== "Closed" && <MenuItem onClick={() => handleCloseTicket(ticket._id)} color="red.500">Close Ticket</MenuItem>}
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        <Flex justifyContent="space-between" alignItems="center" mt={4}>
          <Text fontSize="sm" color="gray.500">Showing {filteredTickets.length} of {pagination.total} tickets</Text>
          <HStack>
            <Select size="sm" width="80px" value={pagination.limit} onChange={(e) => { setPagination(prev => ({ ...prev, limit: Number(e.target.value), currentPage: 1 })); }}>
              <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
            </Select>
            <Button size="sm" onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(prev.currentPage - 1, 1) }))} isDisabled={pagination.currentPage === 1}>Previous</Button>
            <Text fontSize="sm">Page {pagination.currentPage} of {pagination.totalPages}</Text>
            <Button size="sm" onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.currentPage + 1, prev.totalPages) }))} isDisabled={pagination.currentPage === pagination.totalPages}>Next</Button>
          </HStack>
        </Flex>
      </Box>

      {/* Create Ticket Modal */}
      <Modal isOpen={isCreateOpen} onClose={onCreateClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Ticket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl isRequired><FormLabel>Type</FormLabel>
                    <Select value={newTicket.type} onChange={(e) => setNewTicket({ ...newTicket, type: e.target.value })}>
                      {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </Select>
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isRequired><FormLabel>Category</FormLabel>
                    <Select value={newTicket.category} onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}>
                      {categoryOptions.map(c => <option key={c} value={c}>{c}</option>)}
                    </Select>
                  </FormControl>
                </GridItem>
              </Grid>
              <FormControl isRequired><FormLabel>Priority</FormLabel>
                <Select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}>
                  {priorityOptions.map(p => <option key={p} value={p}>{p}</option>)}
                </Select>
              </FormControl>
              <FormControl isRequired><FormLabel>Subject</FormLabel>
                <Input placeholder="Enter ticket subject" value={newTicket.subject} onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })} />
              </FormControl>
              <FormControl isRequired><FormLabel>Description</FormLabel>
                <Textarea placeholder="Describe the issue in detail" rows={4} value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} />
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCreateClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleCreateTicket}>Create Ticket</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assign Ticket Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Ticket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <FormControl>
                <FormLabel>Select Role</FormLabel>
                <Select placeholder="Select role" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
                  {roles.map(role => <option key={role._id} value={role._id}>{role.roleName}</option>)}
                </Select>
              </FormControl>
              <FormControl isDisabled={!selectedRole}>
                <FormLabel>Select User</FormLabel>
                <Select placeholder={selectedRole ? "Select user" : "Please select a role first"} value={selectedAdmin} onChange={(e) => setSelectedAdmin(e.target.value)}>
                  {admins.map(admin => <option key={admin._id} value={admin._id}>{admin.name} - {admin.department || admin.designation || "Admin"}</option>)}
                </Select>
              </FormControl>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleAssignTicket} isDisabled={!selectedRole || !selectedAdmin}>Assign</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Ticket Details Modal */}
      <Modal isOpen={isDetailsOpen} onClose={onDetailsClose} size="xl">
        <ModalOverlay />
        <ModalContent maxW="800px">
          <ModalHeader>
            <VStack align="start" spacing={2}>
              <Text>{selectedTicket?.ticketId}</Text>
              <HStack spacing={4}>
                <Badge colorScheme={statusColorMap[selectedTicket?.status] || "gray"}>{selectedTicket?.status}</Badge>
                <Badge colorScheme={priorityColorMap[selectedTicket?.priority] || "gray"}>{selectedTicket?.priority}</Badge>
              </HStack>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedTicket && (
              <Stack spacing={4}>
                <Box>
                  <Text fontWeight="bold" fontSize="lg">{selectedTicket.subject}</Text>
                  <Text color="gray.600" mt={2}>{selectedTicket.description}</Text>
                </Box>
                <Divider />
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <GridItem><Text fontSize="sm" color="gray.500">Type</Text><Text fontWeight="medium">{selectedTicket.type}</Text></GridItem>
                  <GridItem><Text fontSize="sm" color="gray.500">Category</Text><Text fontWeight="medium">{selectedTicket.category}</Text></GridItem>
                  <GridItem><Text fontSize="sm" color="gray.500">Created By</Text><Text fontWeight="medium">{selectedTicket.createdBy?.name || "-"} ({selectedTicket.createdBy?.mobile || "-"})</Text></GridItem>
                  <GridItem><Text fontSize="sm" color="gray.500">Assigned To</Text><Text fontWeight="medium">{selectedTicket.assignedTo?.name || "Unassigned"}</Text></GridItem>
                  <GridItem><Text fontSize="sm" color="gray.500">Created At</Text><Text fontWeight="medium">{formatDate(selectedTicket.createdAt)}</Text></GridItem>
                  <GridItem><Text fontSize="sm" color="gray.500">Resolved At</Text><Text fontWeight="medium">{formatDate(selectedTicket.resolvedAt)}</Text></GridItem>
                </Grid>
                <Divider />
                <HStack spacing={2} flexWrap="wrap">
                  <Text fontWeight="medium" w="100%">Quick Actions:</Text>
                  <Button size="sm" onClick={() => handleAssignClick(selectedTicket)}>Assign</Button>
                  <Button size="sm" colorScheme="yellow" onClick={() => { handleUpdateStatus(selectedTicket._id, "In Progress"); onDetailsClose(); }}>Mark In Progress</Button>
                  <Button size="sm" colorScheme="green" onClick={() => { handleUpdateStatus(selectedTicket._id, "Resolved"); onDetailsClose(); }}>Mark Resolved</Button>
                  {selectedTicket.status !== "Closed" && <Button size="sm" colorScheme="red" onClick={() => { handleCloseTicket(selectedTicket._id); onDetailsClose(); }}>Close Ticket</Button>}
                </HStack>
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default Complaints;
